import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions
} from '@tanstack/react-query';
import {receiptsApiClient} from '#lib/api-client';
import {queryKeys} from '#lib/query-client';
import {STALE_TIME, GC_TIME} from '#constants';

export interface CreateSessionResponse {
  data?: {
    stateToken: string;
    refreshToken: string;
    expiresAt: string;
  };
  stateToken?: string;
  refreshToken?: string;
  expiresAt?: string;
}

export enum CardStatus {
  active = 'ACTIVE'
}

export enum CardType {
  BANKAXEPT = 'BANKAXEPT',
  VISA = 'VISA',
  MASTERCARD = 'MASTERCARD',
  DINERS = 'DINERS',
  AMERICAN_EXPRESS = 'AMERICAN_EXPRESS',
  DISCOVER = 'DISCOVER',
  JCB = 'JCB',
  UNIONPAY = 'UNIONPAY',
  PAYPASS = 'PAYPASS',
  MAESTRO = 'MAESTRO',
  UNKNOWN = 'UNKNOWN'
}

export interface LinkedCard {
  cardId: string;
  cardExpiryDate: string;
  maskedPan: string;
  cardIssuer: string;
  cardholderName: string;
  cardLinkStatus: boolean;
  cardType: CardType;
  status: CardStatus;
}

export interface LinkedCardsResponse {
  data?: {
    linkedPaymentCards: LinkedCard[];
  };
}

// Hook for creating receipts session
export function useCreateReceiptsSession(
  options?: UseMutationOptions<CreateSessionResponse, Error, void>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await receiptsApiClient.createSession();
      return response as CreateSessionResponse;
    },
    onSuccess: () => {
      // Invalidate linked cards query to refresh data
      queryClient.invalidateQueries({
        queryKey: queryKeys.receipts.linkedCards()
      });
    },
    ...options
  });
}

// Hook for getting linked cards
export function useGetLinkedCards(
  options?: Omit<UseQueryOptions<LinkedCardsResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.receipts.linkedCards(),
    queryFn: async () => {
      const response = await receiptsApiClient.getLinkedCards();
      return response as LinkedCardsResponse;
    },
    staleTime: STALE_TIME.RECEIPTS,
    gcTime: GC_TIME.RECEIPTS,
    ...options
  });
}

// Hook for unlinking a card
export function useUnlinkCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number | string) => receiptsApiClient.unlinkCard(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.receipts.linkedCards()
      });
    }
  });
}

// Legacy exports for backward compatibility (can be removed after migration)
export const useCreateSessionMutation = useCreateReceiptsSession;
export const useGetLinkedCardsQuery = useGetLinkedCards;
export const useUnlinkCardMutation = useUnlinkCard;
